import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthProvider';
import { getDashboard, type DashboardAggregates } from '../lib/advisorApi';
import './advisor.css';

export function DashboardScreen() {
  const { api, user } = useAuth();
  const [data, setData] = useState<DashboardAggregates | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const d = await getDashboard(api);
        if (!cancelled) setData(d);
      } catch {
        if (!cancelled) setError('We could not load your book just now.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  const pending = data?.pendingAttestationsCount ?? 0;

  return (
    <div className="la-rise">
      <header className="page__head">
        <p className="la-label">
          {user?.firmAdmin === true ? 'Firm-wide' : 'Your book'}
        </p>
        <h1 className="page__title">Overview</h1>
        <p className="page__sub">
          What your clients have entrusted to the vault — and what needs you this week.
        </p>
      </header>

      {error !== null && (
        <p className="auth__alert" role="alert">
          {error}
        </p>
      )}

      {data !== null && (
        <>
          <div className="stats">
            <div className="la-card stat">
              <p className="la-label">Clients</p>
              <p className="stat__value">{data.totalClients}</p>
            </div>
            <div className="la-card stat">
              <p className="la-label">Messages sealed</p>
              <p className="stat__value">{data.sealedMessageCount}</p>
            </div>
            <div className="la-card stat">
              <p className="la-label">Awaiting attestation</p>
              <p className={`stat__value${pending > 0 ? ' stat__value--attention' : ''}`}>
                {pending}
              </p>
            </div>
            <div className="la-card stat">
              <p className="la-label">Upcoming ({data.eventsHorizonDays}d)</p>
              <p className="stat__value">{data.upcomingEventsCount}</p>
            </div>
            <div className="la-card stat">
              <p className="la-label">Delivered ({data.recentlyDeliveredDays}d)</p>
              <p className="stat__value">{data.recentlyDeliveredCount}</p>
            </div>
          </div>

          {pending > 0 && (
            <div className="la-card formCard">
              <p className="la-label">Needs you</p>
              <h2>
                {pending} {pending === 1 ? 'client is' : 'clients are'} waiting to be attested
              </h2>
              <p>
                A client cannot seal a message until you confirm they are who they say they are.
                Until then, their vault stays open and nothing is delivered.
              </p>
              <div className="la-cta-wrap">
                <Link to="/attestations" className="la-btn-primary">
                  Review queue
                </Link>
              </div>
            </div>
          )}

          <p className="la-card privacyNote">
            You are looking at metadata — how many messages exist, when they are due, whether a
            client has checked in. You are never shown what a message says. The contents are
            encrypted to the recipient, and no key held by this firm can open them.
          </p>
        </>
      )}
    </div>
  );
}
